import dayjs from '../../shared/utils/dayJsFiLocale';
import { FinnishDateFormat } from '../../shared/utils/dateHelpers';
import {
  IngredientDto,
  OutOfStockIngredientDto,
  OutOfStockProductDto,
  ProductDto,
} from '@kotipizzagroup/kotipizza-products-api-client';

export type OutOfStockItemType = 'ingredient' | 'product';

// The fi locale abbreviates weekdays in lowercase ("ma"); these labels want a capital.
const withWeekday = (date: dayjs.Dayjs, dateFormat: string): string => {
  const weekday = date.format('ddd');
  return `${weekday.charAt(0).toUpperCase()}${weekday.slice(1)} ${date.format(dateFormat)}`;
};

/** Full return date for the list: "Ma 20.6.2026". */
export const formatReturnDate = (date: string | dayjs.Dayjs): string => withWeekday(dayjs(date), FinnishDateFormat);

/** Short form for the return-date options, where the year is noise: "Ma 20.6.". */
export const formatReturnDateOption = (date: dayjs.Dayjs): string => withWeekday(date, 'D.M.');

export interface OutOfStockItem {
  itemId: number;
  externalShopId: string;
  type: OutOfStockItemType;
  scheduledUntil?: string | null;
  checked?: boolean;
  name?: string | null;
  /** All itemIds represented by this row - more than one when duplicate ingredients sharing a warehouseItemId were merged. */
  groupedItemIds: number[];
}

export const getIngredientName = (ingredientId: number, ingredients?: IngredientDto[]): string | null | undefined => {
  return ingredients?.find((ingredient) => ingredient.ingredientId === ingredientId)?.name;
};

export const getProductName = (productId: number, products?: ProductDto[]): string | null | undefined => {
  return products?.find((product) => product.productId === productId)?.name;
};

export const combineOutOfStockItems = (
  ingredients?: OutOfStockIngredientDto[],
  products?: OutOfStockProductDto[],
  allIngredients?: IngredientDto[],
  allProducts?: ProductDto[]
): OutOfStockItem[] => {
  if (allIngredients && allProducts) {
    if (ingredients && products) {
      return [
        ...ingredients.map((ingredient) => mapIngredientToOutOfStockItem(ingredient, allIngredients)),
        ...products.map((product) => mapProductToOutOfStockItem(product, allProducts)),
      ];
    }
    if (ingredients) {
      return ingredients.map((ingredient) => mapIngredientToOutOfStockItem(ingredient, allIngredients));
    }
    if (products) {
      return products.map((product) => mapProductToOutOfStockItem(product, allProducts));
    }
    return [];
  }
  return [];
};

const mapIngredientToOutOfStockItem = (
  ingredient: OutOfStockIngredientDto,
  allIngredients: IngredientDto[]
): OutOfStockItem => ({
  itemId: ingredient.ingredientId,
  externalShopId: ingredient.externalShopId,
  type: 'ingredient',
  scheduledUntil: ingredient.scheduledUntil,
  name: getIngredientName(ingredient.ingredientId, allIngredients),
  groupedItemIds: [ingredient.ingredientId],
});

const mapProductToOutOfStockItem = (product: OutOfStockProductDto, allProducts: ProductDto[]): OutOfStockItem => ({
  itemId: product.productId,
  externalShopId: product.externalShopId,
  type: 'product',
  scheduledUntil: product.scheduledUntil,
  name: getProductName(product.productId, allProducts),
  groupedItemIds: [product.productId],
});

/**
 * Merges out-of-stock rows that represent the same underlying stock into a single row, so the
 * listing never shows (and never fires more than one restore/update request for) what is really
 * one physical item:
 *  - ingredients sharing the same warehouseItemId (e.g. "Ananas" and "Ananas (uunin jälkeen)"), and
 *  - exact duplicate (type, itemId) entries, which can happen if the API returns the same
 *    out-of-stock record more than once.
 * The first-seen item in each group becomes the representative row; its `groupedItemIds` collects
 * every itemId in the group so callers can act on all of them.
 */
export const groupDuplicateIngredients = (
  items: OutOfStockItem[],
  allIngredients?: IngredientDto[]
): OutOfStockItem[] => {
  const warehouseItemIdByIngredientId = new Map<number, string>();
  allIngredients?.forEach((ingredient) => {
    if (ingredient.warehouseItemId && ingredient.ingredientId) {
      warehouseItemIdByIngredientId.set(ingredient.ingredientId, ingredient.warehouseItemId);
    }
  });

  const representativeByGroupKey = new Map<string, OutOfStockItem>();
  const grouped: OutOfStockItem[] = [];

  items.forEach((item) => {
    const warehouseItemId = item.type === 'ingredient' ? warehouseItemIdByIngredientId.get(item.itemId) : undefined;
    const groupKey = warehouseItemId ? `warehouse-${warehouseItemId}` : `${item.type}-${item.itemId}`;

    const existingRepresentative = representativeByGroupKey.get(groupKey);
    if (existingRepresentative) {
      if (!existingRepresentative.groupedItemIds.includes(item.itemId)) {
        existingRepresentative.groupedItemIds.push(item.itemId);
      }
      return;
    }

    const representative = { ...item, groupedItemIds: [...item.groupedItemIds] };
    representativeByGroupKey.set(groupKey, representative);
    grouped.push(representative);
  });

  return grouped;
};
